import React from 'react';
import { Alert, Linking, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useAccount, useAlerts, useConditions, useCredits } from '../../src/data';
import { KakaoButton } from '../../src/KakaoButton';
import { Action, Head, Label, Panel, Tag } from '../../src/ui';
import { D } from '../../src/theme';

/**
 * 내 정보.
 *
 * 카카오 버튼을 "30자를 넘겼을 때" 같은 막히는 자리에만 뒀더니, 일부러 로그인하려는 사람은
 * 들어갈 문이 없었다. 계정이 생긴 이상 상태를 보고 들어오고 나갈 수 있는 자리가 하나 있어야 한다.
 * 막히는 자리의 버튼은 전환 지점이라 그대로 두고, 여기는 언제든 올 수 있는 문으로 둔다.
 */
const PRIVACY = 'https://ai-hunter-9f865.web.app/privacy.html';

export default function Me() {
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const account = useAccount();
  const credits = useCredits();
  const conds = useConditions();
  const alerts = useAlerts();

  const watching = conds.filter((c) => c.active).length;
  const pending = conds.filter((c) => c.active && c.needs_confirm).length;

  async function showWelcome() {
    const { resetWelcome } = await import('../../src/welcome');
    await resetWelcome();
    router.push('/welcome');
  }

  /**
   * 계정 삭제. 되돌릴 수 없어서 두 번 묻는다.
   *
   * Google Play 는 앱에서 계정을 만들 수 있으면 앱 안에 삭제 경로를 두라고 요구한다.
   * 지우는 범위를 두루뭉술하게 적으면 사용자가 뭘 잃는지 모르고 누르게 되므로 그대로 적는다.
   */
  function removeAccount() {
    Alert.alert(
      '계정을 삭제할까요?',
      '카카오 계정 연결, 등록한 조건, 출석 기록, 남은 크레딧이 모두 지워집니다. 되돌릴 수 없어요.',
      [
        { text: '취소', style: 'cancel' },
        {
          text: '계속',
          style: 'destructive',
          onPress: () =>
            Alert.alert('정말 지울까요?', '마지막 확인이에요. 지운 뒤에는 복구할 수 없습니다.', [
              { text: '취소', style: 'cancel' },
              {
                text: '삭제',
                style: 'destructive',
                onPress: async () => {
                  try {
                    const { deleteAccount } = await import('../../src/kakao');
                    await deleteAccount();
                    Alert.alert('삭제했어요', '계정과 관련 데이터를 모두 지웠습니다.');
                  } catch (e) {
                    Alert.alert('삭제하지 못했어요', e instanceof Error ? e.message : '잠시 뒤 다시 시도해 주세요.');
                  }
                },
              },
            ]),
        },
      ],
    );
  }

  function logout() {
    Alert.alert('로그아웃할까요?', '조건과 크레딧은 계정에 남아 있어요. 다시 로그인하면 그대로 보입니다.', [
      { text: '취소', style: 'cancel' },
      {
        text: '로그아웃',
        style: 'destructive',
        onPress: async () => {
          const { signOutFromKakao } = await import('../../src/kakao');
          await signOutFromKakao().catch(() => {});
        },
      },
    ]);
  }

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: D.ground }}
      contentContainerStyle={{ paddingTop: insets.top + 14, paddingBottom: 28, gap: 10 }}
    >
      <Head kicker={account.signedUp ? "카카오로 로그인됨" : "가입하면 크레딧이 쌓여요"} title="내 정보" />

      <View style={{ paddingHorizontal: 16, gap: 10 }}>
      {account.signedUp ? (
        <View style={s.card}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={s.avatar}>
              <Text style={s.avatarText}>{(account.nickname || '?').slice(0, 1)}</Text>
            </View>
            <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
              <Text style={s.name} numberOfLines={1}>{account.nickname || '카카오 사용자'}</Text>
              <Text style={s.sub}>카카오로 로그인됨</Text>
            </View>
            <Pressable onPress={logout} hitSlop={8}>
              <Text style={s.logout}>로그아웃</Text>
            </Pressable>
          </View>
          <Pressable onPress={removeAccount} hitSlop={6} style={s.danger}>
            <Text style={s.dangerText}>계정 삭제</Text>
          </Pressable>
        </View>
      ) : (
        <View style={[s.card, { gap: 12 }]}>
          <View style={{ gap: 5 }}>
            <Text style={s.name}>아직 로그인하지 않았어요</Text>
            <Text style={s.body}>
              가입하면 크레딧 20개를 드려요. 매일 5개씩 더 쌓이고, 긴 문장도 AI 가 읽어 줍니다.
              전부 무료예요.
            </Text>
          </View>
          <KakaoButton />
        </View>
      )}

      <View style={s.card}>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Text style={s.cardTitle}>{account.signedUp ? '크레딧' : '가입하면 받는 것'}</Text>
          {account.signedUp ? <Text style={s.big}>{credits ?? 0}</Text> : null}
        </View>
        <View style={s.rules}>
          <Row label="AI 가 문장 해석" value="3 크레딧" />
          <Row label="AI 되묻기 한 번" value="1 크레딧" />
          <Row label="매일 들어오면" value="+5" good />
          <Row label="가입할 때 한 번" value="+20" good last />
        </View>
        <Text style={s.note}>
          {account.signedUp
            ? '크레딧은 팔지 않아요. 들어오시면 쌓입니다.'
            : '가입 전에도 하루 한 번은 그냥 써 보실 수 있어요. 가입하면 크레딧이 쌓이기 시작합니다.'}
        </Text>
      </View>

      <Pressable onPress={() => router.push('/(tabs)/conditions')} style={({ pressed }) => [s.card, s.link, pressed && { opacity: 0.9 }]}>
        <View style={{ flex: 1, gap: 3 }}>
          <Text style={s.cardTitle}>내 조건</Text>
          <Text style={s.sub}>
            {watching}개 감시 중{pending ? ` · ${pending}개는 답을 기다려요` : ''}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color={D.ink3} />
      </Pressable>

      <Pressable onPress={() => router.push('/(tabs)/alerts')} style={({ pressed }) => [s.card, s.link, pressed && { opacity: 0.9 }]}>
        <View style={{ flex: 1, gap: 3 }}>
          <Text style={s.cardTitle}>받은 알림</Text>
          <Text style={s.sub}>{alerts.length}건</Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color={D.ink3} />
      </Pressable>

      <Pressable onPress={() => Linking.openURL(PRIVACY)} style={({ pressed }) => [s.card, s.link, pressed && { opacity: 0.9 }]}>
        <Text style={[s.cardTitle, { flex: 1 }]}>개인정보 처리방침</Text>
        <Ionicons name="open-outline" size={16} color={D.ink3} />
      </Pressable>

      <Pressable onPress={showWelcome} style={({ pressed }) => [s.card, s.link, pressed && { opacity: 0.9 }]}>
        <View style={{ flex: 1, gap: 3 }}>
          <Text style={s.cardTitle}>앱 소개 다시 보기</Text>
          <Text style={s.sub}>처음 켰을 때 나오는 안내를 다시 봐요</Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color={D.ink3} />
      </Pressable>

      <Text style={s.disclosure}>
        이 서비스는 제휴마케팅이 포함된 광고로 커미션을 지급 받으며 상품 가격에는 영향을 주지 않습니다.
        판매처가 직접 운영하는 공식 서비스가 아닌 독립 큐레이션 서비스입니다.
      </Text>
      </View>
    </ScrollView>
  );
}

/** 받는 쪽(good)은 형광으로 띄운다. 빨강은 이 앱에서 할인율 한 곳에만 쓴다 — 받는 걸 빨갛게 쓰면 잃는 것처럼 읽힌다. */
function Row({ label, value, good, last }: { label: string; value: string; good?: boolean; last?: boolean }) {
  return (
    <View style={[s.row, !last && { borderBottomWidth: 1, borderBottomColor: D.line }]}>
      <Text style={s.rowLabel}>{label}</Text>
      {good ? (
        <View style={s.gain}>
          <Text style={s.gainText}>{value}</Text>
        </View>
      ) : (
        <Text style={s.rowValue}>{value}</Text>
      )}
    </View>
  );
}

const s = StyleSheet.create({
  card: { backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink, padding: 14, gap: 10 },
  link: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  cardTitle: { fontSize: 14.5, fontWeight: '900', color: D.ink },
  name: { fontSize: 16, fontWeight: '900', color: D.ink },
  sub: { fontSize: 12, color: D.ink2 },
  body: { fontSize: 13, color: D.ink2, lineHeight: 20 },
  logout: { fontSize: 12, fontWeight: '800', color: D.ink2 },
  // 되돌릴 수 없는 것이라 눈에 띄되 실수로 누를 자리는 아니게 맨 아래 한 줄로 둔다
  danger: { borderTopWidth: 1, borderTopColor: D.line, paddingTop: 10, alignItems: 'center' },
  dangerText: { fontSize: 12, fontWeight: '800', color: D.alert },
  avatar: { width: 42, height: 42, backgroundColor: '#FEE500', alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontSize: 18, fontWeight: '900', color: '#191600' },
  big: { fontSize: 28, fontWeight: '900', color: D.ink, fontVariant: ['tabular-nums'], letterSpacing: -1 },
  rules: { backgroundColor: D.ground, paddingHorizontal: 12 },
  gain: { backgroundColor: D.volt, borderWidth: 1, borderColor: D.ink, paddingVertical: 1, paddingHorizontal: 6 },
  gainText: { fontSize: 12.5, fontWeight: '900', color: D.ink, fontVariant: ['tabular-nums'] },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 9, gap: 12 },
  rowLabel: { fontSize: 12.5, color: D.ink2 },
  rowValue: { fontSize: 12.5, fontWeight: '900', color: D.ink, fontVariant: ['tabular-nums'] },
  note: { fontSize: 11.5, color: D.ink3, lineHeight: 17 },
  disclosure: { fontSize: 10.5, lineHeight: 16, color: D.ink2, marginTop: 2 },
});
